import { useState, type InputHTMLAttributes, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Eye, EyeOff, Lock, type LucideIcon } from "lucide-react";

interface IconFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  icon: LucideIcon;
  trailing?: ReactNode;
}

// IconField is the auth-form input row from the .pen design: a 13px semibold
// label above a bordered surface pill with a leading lucide icon, the input, and
// an optional trailing control (e.g. a reveal toggle). Amber focus-within ring
// matches the app's global focus treatment across both themes.
export function IconField({ label, icon: Icon, trailing, ...inputProps }: IconFieldProps) {
  return (
    <label className="flex flex-col gap-2">
      <span className="text-[13px] font-semibold text-ink-2">{label}</span>
      <span className="flex h-(--ctl-h) items-center gap-2.5 rounded-xl border border-edge-3 bg-surface px-3.5 focus-within:border-amber">
        <Icon size={16} className="shrink-0 text-muted" />
        <input
          className="w-full bg-transparent text-[15px] text-ink placeholder:text-muted-2 focus:outline-none"
          {...inputProps}
        />
        {trailing}
      </span>
    </label>
  );
}

// PasswordField is an IconField locked to the lock icon with a built-in
// show/hide reveal toggle.
export function PasswordField({
  label,
  ...inputProps
}: Omit<IconFieldProps, "icon" | "type" | "trailing">) {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  return (
    <IconField
      label={label}
      icon={Lock}
      type={show ? "text" : "password"}
      trailing={
        <button
          type="button"
          onClick={() => setShow((v) => !v)}
          className="shrink-0 text-muted hover:text-ink"
          aria-label={show ? t("login.hidePassword") : t("login.showPassword")}
        >
          {show ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      }
      {...inputProps}
    />
  );
}
